import { useState } from "react";
import { toast } from "sonner";
import { FileSignature, CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg, fmtDT, usd } from "../lib/api";
import { Btn, Field, Input, Textarea } from "./kit";
import SignaturePad from "./SignaturePad";
import { PhotoSlot } from "./Photos";

const METHOD = { in_person_signature: "In-person signature", written_attachment: "Written approval (attached)", declined: "Customer declined", absent: "Customer absent" };

function AuthList({ job }) {
  if (!job.authorizations.length) return <div className="text-sm text-neutral-500">No approvals recorded yet.</div>;
  return (
    <div className="space-y-2" data-testid="authorization-list">
      {job.authorizations.map((a) => (
        <div key={a.id} className="rounded-xl border border-[#262626] p-3 text-sm" data-testid="authorization-record">
          <div className="flex justify-between gap-2">
            <span className="font-medium">{a.kind === "work" ? "Work authorization" : "Completion acknowledgment"}</span>
            <span className="font-mono text-xs text-neutral-500">v{a.quote_version}</span>
          </div>
          <div className={`flex items-center gap-1.5 mt-1 ${["declined", "absent"].includes(a.method) ? "text-amber-400" : "text-emerald-400"}`}>
            {["declined", "absent"].includes(a.method) ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
            {METHOD[a.method]}{a.customer_name ? ` · ${a.customer_name}` : ""}
          </div>
          <div className="text-xs text-neutral-500 mt-1">{usd(a.subtotal)} + tax {usd(a.tax_amount)} = {usd(a.total)} · {a.scope}</div>
          {a.note && <div className="text-xs text-neutral-400 mt-1">“{a.note}”</div>}
          {a.signature && <img src={a.signature} alt="signature" className="h-16 mt-2 bg-[#0F0F0F] rounded border border-[#262626]" />}
          <div className="text-[11px] font-mono text-neutral-600 mt-1">Recorded by {a.actor_name} ({a.actor_role}) · server time {fmtDT(a.at)}</div>
        </div>
      ))}
    </div>
  );
}

export default function Approval({ job, user, reload }) {
  const [kind, setKind] = useState(job.gates.work_authorized ? "completion" : "work");
  const [name, setName] = useState(job.customer?.name || "");
  const [sig, setSig] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [padKey, setPadKey] = useState(0);
  const isAdmin = user.role === "admin";
  const price = job.customer_price || job.pricing;
  const scope = job.quote_scope || job.quotes?.[job.quotes.length - 1]?.scope;
  const canRecord = !["completed", "cancelled", "draft", "open"].includes(job.status);
  const approvalPhotos = (job.photos || []).filter((p) => p.phase === "approval");

  const record = async (method) => {
    setBusy(method);
    try {
      await api.post(`/jobs/${job.id}/authorizations`, {
        kind, method, customer_name: name, signature: sig, customer_signing_confirmed: confirm, note,
        photo_id: method === "written_attachment" ? approvalPhotos[approvalPhotos.length - 1]?.id : null,
      });
      toast.success("Recorded");
      setSig(null);
      setConfirm(false);
      setNote("");
      setPadKey((k) => k + 1);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-4" data-testid="approval-panel">
      <div className="rounded-xl border border-[#262626] p-3 space-y-1">
        <div className="text-xs uppercase tracking-wider text-neutral-500">Current quote v{job.current_quote_version}</div>
        <div className="text-sm">{scope}</div>
        <div className="font-mono text-sm" data-testid="approval-quote-totals">Subtotal {usd(price.subtotal)} · Tax {usd(price.tax_amount)} · Total {usd(price.total)}</div>
        <div className={`text-sm ${job.gates.work_authorized ? "text-emerald-400" : "text-amber-400"}`} data-testid="work-authorized-status">
          {job.gates.work_authorized
            ? "Customer approved this quote version"
            : job.current_quote_version > 1
              ? `Revised quote v${job.current_quote_version} needs customer approval before continuing`
              : "Customer approval needed before work starts"}
        </div>
      </div>
      <AuthList job={job} />
      {canRecord && (
        <div className="space-y-3 rounded-xl border border-[#262626] p-3">
          <div className="font-head uppercase tracking-wide flex items-center gap-2"><FileSignature className="w-4 h-4 text-[#FF6B00]" />Record customer response</div>
          <div className="grid grid-cols-2 gap-1 p-1 bg-[#0F0F0F] rounded-xl border border-[#262626]">
            {[["work", "Work approval"], ["completion", "Completion ack."]].map(([k, l]) => (
              <button key={k} onClick={() => setKind(k)} data-testid={`auth-kind-${k}`} className={`h-9 rounded-lg text-xs font-semibold ${kind === k ? "bg-[#1f1f1f] text-white" : "text-neutral-500"}`}>{l}</button>
            ))}
          </div>
          <p className="text-xs text-neutral-400">
            {kind === "work"
              ? `Customer approves: ${scope} — subtotal ${usd(price.subtotal)}, tax ${usd(price.tax_amount)}, total ${usd(price.total)}.`
              : job.ack_text || "I acknowledge the work listed above was performed today. This is not a waiver and does not certify the vehicle as safe."}
          </p>
          <Field label="Customer name (printed)"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-customer-name" /></Field>
          <SignaturePad key={padKey} onChange={setSig} />
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-1 accent-[#FF6B00]" data-testid="auth-customer-confirm" />
            <span>The customer is personally signing on this device. Staff may never sign for the customer.</span>
          </label>
          <Btn className="w-full" onClick={() => record("in_person_signature")} loading={busy === "in_person_signature"} disabled={!sig || !confirm || !name} data-testid="auth-save-signature-btn">Save customer signature</Btn>
          <div className="grid grid-cols-2 gap-2">
            <Btn variant="outline" size="sm" onClick={() => record("declined")} loading={busy === "declined"} data-testid="auth-declined-btn">Customer declined</Btn>
            {kind === "completion" && <Btn variant="outline" size="sm" onClick={() => record("absent")} loading={busy === "absent"} data-testid="auth-absent-btn">Customer absent</Btn>}
          </div>
          {isAdmin && (
            <div className="space-y-2 pt-3 border-t border-[#262626]">
              <div className="text-xs uppercase tracking-wider text-neutral-500">Admin: attach written approval (e.g. Google Voice text)</div>
              <PhotoSlot jobId={job.id} phase="approval" slot="written_approval" label="Screenshot of approval" photos={job.photos} onUploaded={reload} onView={() => {}} />
              <Textarea placeholder="Paste the customer's message and date" value={note} onChange={(e) => setNote(e.target.value)} data-testid="auth-written-note" />
              <Btn variant="outline" className="w-full" onClick={() => record("written_attachment")} loading={busy === "written_attachment"} disabled={!note && !approvalPhotos.length} data-testid="auth-written-save-btn">Record written approval</Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
